import React, {useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";

/* @mui material UI */
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
/* Reusable component */
import {FormButton} from "components/FormComponents/Button/FormButton";
import {FormTextField} from "components/FormComponents/TextField/FormTextField";
/* Axios generic Repository */
import {getRequest, postRequest, setHeader} from "repository/genericRepository";
import {VERIFYCOSTCENTERNAME_ENDPOINT} from "commonUtils/globalAPISettings";
import {CostCenterVerificationSchema} from "commonUtils/schema";
import {useAppSelector, store} from "../../../../redux/store";
import {
  handleAxiosErrors,
  initialMessage,
  isValidObject,
  loggedMessage,
  message,
} from "commonUtils/utils";
import {
  Alerts,
  API_TIMEOUTS_MILLISECONDS,
  PageNames,
} from "commonUtils/enumerations";
import {logMessage} from "services/loggerService";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import DOMPurify from "dompurify";

interface FormValues {
  costCenterName: string;
}

const ConstCenterVerfication: React.FC = () => {
  const {userDetails} = useAppSelector(state => {
    return state.auth;
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: {errors},
  } = useForm<FormValues>({
    resolver: yupResolver(CostCenterVerificationSchema),
  });

  const [loader, setLoader] = useState<boolean>(false);
  const [successmessage, setSuccessMessage] = useState<string>("");
  const [button1, setButton1] = useState<string>("");
  const [button2, setButton2] = useState<string>("");
  const [showhidebutton, setShowHideButton] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [serverValidation, setServerValidation] =
    React.useState<message>(initialMessage);

  const setCostCenterMessages = (successMsg: string) => {
    setSuccessMessage(successMsg);
  };

  React.useEffect(() => {
    setErrorMsg("");
    setSuccessMessage("");
  }, []);

  const onSubmit = async (data: FormValues) => {
    try {
      const {costCenterName} = data;
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      if (button1 === "handleButtonSearch") {
        setLoader(true);
        logMessage(
          "INFO",
          `User ${userDetails?.userName} has clicked the Cost Center Verification search button.`,
          PageNames.DASHBOARD,
        );
        setCostCenterMessages("");
        const responseData = await getRequest({
          url: `${VERIFYCOSTCENTERNAME_ENDPOINT}?costCenterName=${costCenterName}`,
          headers: setHeader(APIHeaders),
          timeout: API_TIMEOUTS_MILLISECONDS.VERIFYCOSTCENTERNAME_ENDPOINT,
          params: {},
        });
        if (isValidObject(responseData)) {
          setCostCenterMessages(responseData?.message);
        }
        setLoader(false);
      } else if (button2 === "handleButtonSubmit") {
        setLoader(true);
        logMessage(
          "INFO",
          `User ${userDetails?.userName} has clicked the Cost Center Verification submit button.`,
          PageNames.DASHBOARD,
        );
        setCostCenterMessages("");
        const profileId = authState?.auth?.userDetails?.profileId;
        const switchedProfileId =
          authState?.auth?.userDetails?.switchedProfileId;
        const responseData = await postRequest({
          url: `${VERIFYCOSTCENTERNAME_ENDPOINT}`,
          headers: setHeader(APIHeaders),
          timeout: API_TIMEOUTS_MILLISECONDS.VERIFYCOSTCENTERNAME_ENDPOINT,
          params: {
            costCenterName,
            profileId,
            switchedProfileId,
          },
        });
        if (
          isValidObject(responseData) &&
          responseData.messageCode === 527396
        ) {
          setCostCenterMessages(responseData?.message);
          setShowHideButton(false);
          setShowAlert(false);
          setServerValidation(initialMessage);
        }
        setLoader(false);
      }
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.DASHBOARD);
      if (error?.response?.data?.messageCode === 527392) {
        setShowHideButton(true);
      } else {
        setShowHideButton(false);
      }
      handleAxiosErrors(error, setShowAlert, setServerValidation);
      setLoader(false);
    }
  };

  const handleButtonSearch = () => {
    setButton1("handleButtonSearch");
    setButton2("");
  };
  const handleButtonSubmit = () => {
    setButton2("handleButtonSubmit");
    setButton1("");
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={event => {
          event.preventDefault();
          handleSubmit(onSubmit)(event);
        }}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography component="span" className="cost-center-verification">
              Cost Center Verification
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <Controller
              name="costCenterName"
              control={control}
              defaultValue=""
              render={({field: {ref, ...field}}) => (
                <FormTextField
                  {...field}
                  label=""
                  id="costCenterName"
                  name="costCenterName"
                  inputRef={ref}
                  placeholder={"Enter Cost Center Number"}
                  fullWidth
                  autoComplete={"costCenterName"}
                  error={!!errors.costCenterName}
                  helperText={
                    errors.costCenterName ? errors.costCenterName?.message : ""
                  }
                  onChange={e => {
                    field.onChange(e);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={3}>
            <FormButton
              onClick={handleButtonSearch}
              type="submit"
              variant="contained"
              outlet="Search"
            />
          </Grid>
          {showhidebutton && (
            <Grid item xs={4}>
              <FormButton
                onClick={handleButtonSubmit}
                type="submit"
                variant="contained"
                outlet="Submit"
              />
            </Grid>
          )}

          <Box pt={1}>
            {showAlert && serverValidation?.message && (
              <FormValidationAlert
                closeIcon={true}
                showAlertBox={showAlert}
                setShowAlertBox={setShowAlert}
                severity={
                  (serverValidation?.isSuccessMessage ?? false)
                    ? Alerts.SUCCESS
                    : (serverValidation?.isValidationMessage ?? false)
                      ? Alerts.INFO
                      : Alerts.ERROR
                }
                message={serverValidation?.message}
              />
            )}
          </Box>

          <Grid item xs={12}>
            {loader && (
              <Box className="display-flex justify-content-center">
                <CircularProgress size={15} />
              </Box>
            )}
            {Object.keys(errors).length === 0 && successmessage && (
              <Box className="success-message-color">
                {
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(successmessage),
                    }}
                  />
                }
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
export default ConstCenterVerfication;
